import type { ConnectActivityEntry } from "@ando/core";
import { Skeleton, cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Activity, AlertCircle, CheckCircle2, XCircle } from "lucide-react";

import { useConnectActivity } from "../hooks/useConnectActivity.js";
import { RailEmptyInline, formatRelative } from "./connectShared.js";

/**
 * ActivityFeed — owner-side access log (Attività tab).
 *
 * Renders the Space's recent access-log entries (actor, when, success, query
 * preview) in reverse-chronological order. NO cost/EUR fields are read or shown
 * (the BE table has none; the customer-facing surface must never display
 * currency).
 *
 * An empty feed (no events yet) and a failed load both fall back to the honest
 * empty-state rather than a scary error spinner for a feature that simply has
 * no activity.
 */

export interface ActivityFeedProps {
  readonly spaceId: string;
  readonly enabled?: boolean;
}

export function ActivityFeed({ spaceId, enabled = true }: ActivityFeedProps) {
  const { i18n } = useLingui();
  const { entries, isLoading, isError } = useConnectActivity(spaceId, {
    enabled,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 py-2">
        {["a1", "a2", "a3"].map((k) => (
          <Skeleton key={k} shape="rect" className="h-12 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  // Empty feed OR a failed load → ONE honest empty-state, so the owner never
  // sees a scary error for a feature that simply has no events.
  if (isError || entries.length === 0) {
    return (
      <RailEmptyInline
        icon={<Activity size={16} strokeWidth={1.75} />}
        title={i18n._(msg`Nessuna attività`)}
        body={i18n._(msg`Le domande dei tuoi ospiti e le azioni sullo spazio appariranno qui.`)}
      />
    );
  }

  return (
    <ul className="flex flex-col gap-2 py-2">
      {entries.map((entry, idx) => (
        <ActivityRow key={activityKey(entry, idx)} entry={entry} locale={i18n.locale} />
      ))}
    </ul>
  );
}
ActivityFeed.displayName = "ActivityFeed";

function ActivityRow({
  entry,
  locale,
}: {
  readonly entry: ConnectActivityEntry;
  readonly locale: string;
}) {
  const when = formatRelative(entry.created_at, locale);
  const actor = entry.actor_label?.trim() || null;
  const ok = entry.success;

  return (
    <li className="flex items-start gap-2.5 rounded-lg border border-hairline/70 bg-white/55 px-3 py-2.5">
      <span
        aria-hidden
        className={cn(
          "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
          ok ? "bg-success/15 text-success" : "bg-rose-50 text-rose-600",
        )}
      >
        {ok ? <CheckCircle2 size={13} strokeWidth={2} /> : <XCircle size={13} strokeWidth={2} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate font-medium text-[12px] text-ink-1">
            {actor ?? <Trans>Ospite</Trans>}
          </p>
          {when ? <span className="shrink-0 text-[10.5px] text-ink-2">{when}</span> : null}
        </div>
        {entry.query_preview ? (
          <p className="mt-0.5 line-clamp-2 text-[11.5px] text-ink-2">“{entry.query_preview}”</p>
        ) : null}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[10.5px] text-ink-2">
          {typeof entry.row_count === "number" ? (
            <span className="tabular-nums">
              {entry.row_count === 1 ? (
                <Trans>1 risultato</Trans>
              ) : (
                <Trans>{entry.row_count} risultati</Trans>
              )}
            </span>
          ) : null}
          {!ok && entry.error ? (
            <span className="inline-flex items-center gap-1 text-rose-600">
              <AlertCircle size={11} strokeWidth={2} aria-hidden />
              <span className="truncate">{entry.error}</span>
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function activityKey(entry: ConnectActivityEntry, idx: number): string {
  if (typeof entry.id === "string") return entry.id;
  return `${entry.created_at ?? "?"}-${idx}`;
}
